// ABOUTME: Views dropdown ("Tabbladen") for TabView3's pinned mode — lists every view of a
// ABOUTME: screen grouped and searchable, with pin toggles and an attention dot on the trigger.

import { memo, useCallback, useMemo, useState } from "react";
import { Box, ButtonBase, InputBase, useTheme } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";

import { tokens } from "../../../theme";
import PopoverModal from "../Modal/PopoverModal";
import Text from "../text/Text";

/**
 * ViewsMenu
 *
 * Presentational — all state changes go through the callbacks.
 *
 * Props:
 *   views        — [{ key, title (i18n key), group, count?, severity? }] in canonical order
 *   groups       — [{ key, title (i18n key) }] in display order
 *   pinnedKeys   — view keys currently pinned as tabs
 *   activeKey    — key of the view currently shown
 *   onSelectView — (key) => void, open a view (closes the menu)
 *   onTogglePin  — (key) => void, pin/unpin a view (menu stays open)
 */
const ViewsMenu = memo(function ViewsMenu({
    views,
    groups,
    pinnedKeys,
    activeKey,
    onSelectView,
    onTogglePin,
}) {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [anchorEl, setAnchorEl] = useState(null);
    const [search, setSearch] = useState("");
    const open = Boolean(anchorEl);

    const handleOpen = useCallback((e) => setAnchorEl(e.currentTarget), []);
    const handleClose = useCallback(() => {
        setAnchorEl(null);
        setSearch("");
    }, []);

    const handleSelect = useCallback((key) => {
        if (onSelectView) onSelectView(key);
        handleClose();
    }, [onSelectView, handleClose]);

    const unpinnedCount = views.filter((view) => !pinnedKeys.includes(view.key)).length;

    // Urgent work hiding in the dropdown: an unpinned attention view with items.
    const hasUnpinnedAttention = views.some((view) =>
        view.severity === "attention" &&
        !pinnedKeys.includes(view.key) &&
        view.count > 0
    );

    const filteredViews = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return views;
        return views.filter((view) => t(view.title).toLowerCase().includes(query));
    }, [views, search, t]);

    const dotColor = (view) => {
        if (view.severity === "attention") return colors.red[400];
        if (view.count > 0) {
            return theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400];
        }
        return colors.txt.secondary;
    };

    return (
        <>
            <ButtonBase
                onClick={handleOpen}
                sx={{
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    height: 32,
                    my: "auto",
                    borderRadius: 4,
                    fontFamily: "Roboto, sans-serif",
                    fontSize: 14,
                    fontWeight: 500,
                    color: hasUnpinnedAttention ? colors.red[400] : colors.txt.primary,
                    background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                    boxShadow: theme.palette.mode === "dark" ? null : `0px 1px 2px ${colors.grey[200]}`,
                }}
            >
                {hasUnpinnedAttention && (
                    <Box
                        data-testid="views-menu-attention-dot"
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: colors.red[400],
                            flexShrink: 0,
                        }}
                    />
                )}
                {t("more_views")}
                {unpinnedCount > 0 && (
                    <Box component="span" sx={{ color: colors.txt.secondary }}>
                        {unpinnedCount}
                    </Box>
                )}
                <KeyboardArrowDownIcon fontSize="small" sx={{ color: colors.txt.secondary }} />
            </ButtonBase>

            <PopoverModal
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                triangle={{ position: "right", color: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0] }}
                sx={{
                    body: {
                        width: 320,
                        maxHeight: 440,
                        display: "flex",
                        flexDirection: "column",
                        background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                        borderRadius: 3,
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 2,
                        py: 1,
                        borderBottom: `1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                    }}
                >
                    <SearchIcon fontSize="small" sx={{ color: colors.txt.secondary }} />
                    <InputBase
                        autoFocus
                        placeholder={t("search_view")}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ flex: 1, fontSize: 14, color: colors.txt.primary }}
                    />
                </Box>

                <Box sx={{ overflowY: "auto", flex: 1, py: 0.5 }}>
                    {groups.map((group) => {
                        const groupViews = filteredViews.filter((view) => view.group === group.key);
                        if (groupViews.length === 0) return null;
                        const groupPinnedCount = groupViews.filter((view) => pinnedKeys.includes(view.key)).length;

                        return (
                            <Box key={group.key} data-testid={`views-menu-group-${group.key}`}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        px: 2,
                                        pt: 1.5,
                                        pb: 0.5,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            fontFamily: "Roboto, sans-serif",
                                            fontSize: 11,
                                            fontWeight: 700,
                                            letterSpacing: "0.08em",
                                            textTransform: "uppercase",
                                            color: colors.txt.secondary,
                                        }}
                                    >
                                        {t(group.title)}
                                    </Box>
                                    {groupPinnedCount > 0 && (
                                        <Box
                                            sx={{
                                                fontFamily: "Roboto, sans-serif",
                                                fontSize: 12,
                                                color: colors.txt.secondary,
                                            }}
                                        >
                                            {t("group_pinned_count", { count: groupPinnedCount })}
                                        </Box>
                                    )}
                                </Box>

                                {groupViews.map((view) => {
                                    const isPinned = pinnedKeys.includes(view.key);
                                    const isActive = view.key === activeKey;

                                    return (
                                        <Box
                                            key={view.key}
                                            data-testid={`views-menu-row-${view.key}`}
                                            aria-current={isActive ? "true" : undefined}
                                            onClick={() => handleSelect(view.key)}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.5,
                                                px: 2,
                                                height: 40,
                                                cursor: "pointer",
                                                fontFamily: "Roboto, sans-serif",
                                                fontSize: 14,
                                                color: colors.txt.primary,
                                                background: isActive
                                                    ? (theme.palette.mode === "dark" ? colors.grey[400] : colors.bg.tertiary)
                                                    : null,
                                                "&:hover": {
                                                    background: theme.palette.mode === "dark" ? colors.grey[400] : colors.bg.tertiary,
                                                },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: "50%",
                                                    background: dotColor(view),
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Box
                                                sx={{
                                                    flex: 1,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                <Text medium>{t(view.title)}</Text>
                                            </Box>
                                            {view.count > 0 && (
                                                <Box
                                                    sx={{
                                                        background: theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                                                        color: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                                                        height: 24,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        lineHeight: "unset",
                                                        fontFamily: "Roboto, sans-serif",
                                                        borderRadius: 3,
                                                        fontSize: "14px",
                                                        fontWeight: "bold",
                                                        px: 1,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {view.count}
                                                </Box>
                                            )}
                                            <ButtonBase
                                                data-testid={`views-menu-pin-${view.key}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (onTogglePin) onTogglePin(view.key);
                                                }}
                                                sx={{
                                                    flexShrink: 0,
                                                    px: 1.5,
                                                    height: 26,
                                                    borderRadius: 3,
                                                    fontFamily: "Roboto, sans-serif",
                                                    fontSize: 12,
                                                    fontWeight: 500,
                                                    color: isPinned
                                                        ? (theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400])
                                                        : colors.txt.secondary,
                                                    background: isPinned
                                                        ? (theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100])
                                                        : "transparent",
                                                    border: isPinned
                                                        ? null
                                                        : `1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                                                }}
                                            >
                                                {isPinned ? t("tab_pinned") : t("pin_tab")}
                                            </ButtonBase>
                                        </Box>
                                    );
                                })}
                            </Box>
                        );
                    })}
                </Box>

                <Box
                    data-testid="views-menu-footer"
                    sx={{
                        px: 2,
                        py: 1,
                        borderTop: `1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                        fontFamily: "Roboto, sans-serif",
                        fontSize: 12,
                        color: colors.txt.secondary,
                    }}
                >
                    {t("tabs_pinned_footer", { pinned: pinnedKeys.length })}
                </Box>
            </PopoverModal>
        </>
    );
});

export default ViewsMenu;
